<template>
  <div
    class="hestia-iframe-view"
    data-testid="website-builder-hestia-iframe-view"
  >
    <div class="hestia-iframe-container">
      <div
        v-if="showDesignModeSelectorInEditor"
        class="hestia-admin-mode-control"
        data-testid="website-builder-design-mode-control"
      >
        <OwnSegmentedControl
          v-if="designModeSelectorExpanded"
          id="website-builder-design-mode-selector"
          v-model="designModePreference"
          :options="designModeOptions"
          class="hestia-admin-mode-selector"
          data-testid="website-builder-design-mode-selector"
        />
        <button
          class="hestia-admin-mode-trigger"
          type="button"
          aria-controls="website-builder-design-mode-selector"
          :aria-expanded="designModeSelectorExpanded"
          :aria-label="DESIGN_MODE_SELECTOR_LABEL"
          data-testid="website-builder-design-mode-selector-trigger"
          @click="designModeSelectorExpanded = !designModeSelectorExpanded"
        >
          <span>{{ visibleDesignModeLabel }}</span>
          <span
            :class="[
              'hestia-admin-mode-trigger-chevron',
              {
                'hestia-admin-mode-trigger-chevron--expanded':
                  designModeSelectorExpanded,
              },
            ]"
            aria-hidden="true"
          />
        </button>
      </div>
      <div v-if="hestiaError" class="hestia-iframe-error">
        {{ hestiaError }}
      </div>
      <div v-if="!hestiaReady && !hestiaError" class="hestia-iframe-loading">
        <OwnProgressSpinner />
      </div>
      <iframe
        :key="hestiaIframeKey"
        ref="hestiaIframeRef"
        :src="hestiaIframeSrc"
        allow="microphone"
        :class="['hestia-iframe', { 'hestia-iframe--hidden': !hestiaReady }]"
        :title="iframeTitle"
        data-testid="website-builder-hestia-iframe"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

import { useCoreStore } from '@/core/store/useCoreStore'
import {
  buildParentWebsiteLeaveCancelMessage,
  buildParentWebsiteLeaveRequestMessage,
  isHestiaEventsNavigateMessage,
  isHestiaWebsiteAgentSidebarTabSyncMessage,
  isHestiaWebsiteDraftStatusMessage,
  isHestiaWebsiteLeaveActionStartedMessage,
  isHestiaWebsiteLeaveResultMessage,
  type HestiaWebsiteAgentSidebarTab,
} from '@/features/hestia/composables/hestiaIframeEvents'
import { useHestiaIframe } from '@/features/hestia/composables/UseHestiaIframe'
import { useWebsiteBuilderDesignModePreference } from '@/features/website-builder/composables/useWebsiteBuilderDesignModePreference'
import { OwnProgressSpinner } from '@/ui/OwnProgressSpinner'
import { OwnSegmentedControl } from '@/ui/OwnSegmentedControl'
import { useReactiveFeatureFlag } from '@/utils/useFeatureFlags'

const props = defineProps<{
  fixedTargetPath?: boolean
  targetPath?: string
  locationId?: string
  brandScopedTarget?: boolean
}>()

const DESIGN_MODE_SELECTOR_LABEL = 'Website builder design mode'
const route = useRoute()
const router = useRouter()
const { activeBrandId } = storeToRefs(useCoreStore())
const { enabled: savePublishRedesignEnabled } = useReactiveFeatureFlag(
  'hestia.websiteSavePublishRedesign'
)

const hestiaPath = computed(() => {
  if (props.targetPath !== undefined) return props.targetPath
  const value = route.meta?.hestiaPath
  return typeof value === 'string' ? value : undefined
})
const {
  designModeExtraData,
  designModeOptions,
  designModePreference,
  designModeTargetPath,
  showDesignModeSelector,
} = useWebsiteBuilderDesignModePreference({
  brandId: activeBrandId,
  variantBTargetPath: hestiaPath,
})
const websiteAgentSidebarTab = ref<HestiaWebsiteAgentSidebarTab | null>(null)
const designModeSelectorExpanded = ref(false)
const showDesignModeSelectorInEditor = computed(
  () =>
    !props.fixedTargetPath &&
    showDesignModeSelector.value &&
    websiteAgentSidebarTab.value === 'editor'
)
const visibleDesignModeLabel = computed(
  () =>
    designModeOptions.find(({ value }) => value === designModePreference.value)
      ?.label ?? 'Mode'
)

const hasUnpublishedChanges = ref(false)
const LEAVE_REQUEST_TIMEOUT_MS = 10_000
const LEAVE_ACTION_TIMEOUT_MS = 120_000
let pendingLeaveRequest:
  | {
      promise: Promise<boolean>
      requestId: string
      resolve: (shouldLeave: boolean) => void
      timeoutId: ReturnType<typeof setTimeout>
    }
  | undefined

const resolvePendingLeaveRequest = (
  shouldLeave: boolean,
  options: { notifyHestiaCancel?: boolean } = {}
): void => {
  const request = pendingLeaveRequest
  pendingLeaveRequest = undefined
  if (!request) return
  clearTimeout(request.timeoutId)
  if (options.notifyHestiaCancel) {
    postToHestia(buildParentWebsiteLeaveCancelMessage(request.requestId))
  }
  request.resolve(shouldLeave)
}

const extendPendingLeaveTimeout = (requestId: string): void => {
  const request = pendingLeaveRequest
  if (!request || request.requestId !== requestId) return
  clearTimeout(request.timeoutId)
  request.timeoutId = setTimeout(
    () => resolvePendingLeaveRequest(false, { notifyHestiaCancel: true }),
    LEAVE_ACTION_TIMEOUT_MS
  )
}

const handleHestiaMessage = (message: unknown): void => {
  if (isHestiaWebsiteAgentSidebarTabSyncMessage(message)) {
    websiteAgentSidebarTab.value = message.tab
    return
  }
  if (isHestiaEventsNavigateMessage(message)) {
    void router.push({ name: 'brand.events' })
    return
  }
  if (isHestiaWebsiteDraftStatusMessage(message)) {
    hasUnpublishedChanges.value =
      savePublishRedesignEnabled.value && message.hasUnpublishedChanges
    return
  }
  if (isHestiaWebsiteLeaveActionStartedMessage(message)) {
    extendPendingLeaveTimeout(message.requestId)
    return
  }
  if (!isHestiaWebsiteLeaveResultMessage(message)) return
  if (message.requestId !== pendingLeaveRequest?.requestId) return
  resolvePendingLeaveRequest(message.shouldLeave)
}

const {
  error: hestiaError,
  iframeKey: hestiaIframeKey,
  iframeRef: hestiaIframeRef,
  iframeSrc: hestiaIframeSrc,
  isReady: hestiaReady,
  isSessionHydrated: hestiaSessionHydrated,
  postToHestia,
} = useHestiaIframe({
  brandId: activeBrandId,
  locationId: computed(() => props.locationId),
  rootTargetPath: computed(() =>
    props.brandScopedTarget &&
    !props.locationId &&
    activeBrandId.value &&
    props.targetPath
      ? `/brands/${activeBrandId.value}${props.targetPath}`
      : undefined
  ),
  embed: 'website-builder-hermes',
  extraData: computed(() =>
    props.fixedTargetPath ? undefined : designModeExtraData.value
  ),
  onMessage: handleHestiaMessage,
  targetPath: computed(() =>
    props.fixedTargetPath ? hestiaPath.value : designModeTargetPath.value
  ),
})
const hasBlockingUnpublishedChanges = computed(
  () =>
    savePublishRedesignEnabled.value &&
    hasUnpublishedChanges.value &&
    !hestiaError.value
)

const cancelPendingLeaveRequest = (): void => {
  resolvePendingLeaveRequest(false, { notifyHestiaCancel: true })
}

const cancelIframeLeaveRequest = (): void => {
  cancelPendingLeaveRequest()
}

watch(hestiaIframeKey, cancelIframeLeaveRequest)
watch(designModePreference, () => {
  designModeSelectorExpanded.value = false
})
watch(showDesignModeSelectorInEditor, (visible) => {
  if (visible) return
  designModeSelectorExpanded.value = false
})
watch(hestiaError, (error) => {
  if (!error) return
  hasUnpublishedChanges.value = false
  cancelPendingLeaveRequest()
})

onBeforeRouteLeave(() => {
  if (!hasBlockingUnpublishedChanges.value) return true
  // A remount clears the iframe's observed origin, so posting while the new
  // frame is still hydrating would silently drop the request.
  if (!hestiaReady.value || !hestiaSessionHydrated.value) return false
  if (pendingLeaveRequest) return pendingLeaveRequest.promise
  const requestId = crypto.randomUUID()
  let resolveLeave!: (shouldLeave: boolean) => void
  const promise = new Promise<boolean>((resolve) => {
    resolveLeave = resolve
  })
  const timeoutId = setTimeout(
    cancelPendingLeaveRequest,
    LEAVE_REQUEST_TIMEOUT_MS
  )
  // Register before posting so a synchronous response cannot be dropped.
  pendingLeaveRequest = { promise, requestId, resolve: resolveLeave, timeoutId }
  postToHestia(buildParentWebsiteLeaveRequestMessage(requestId))
  return promise
})

const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
  if (!hasBlockingUnpublishedChanges.value) return
  event.preventDefault()
  event.returnValue = ''
}

onMounted(() => window.addEventListener('beforeunload', handleBeforeUnload))
onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
  cancelPendingLeaveRequest()
})

const iframeTitle = 'Owner App'
</script>

<style scoped lang="scss">
.hestia-iframe-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.hestia-iframe-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  position: relative;
}

.hestia-admin-mode-control {
  position: absolute;
  bottom: 12px;
  right: 12px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  width: min(336px, calc(100% - 24px));
}

.hestia-admin-mode-selector {
  width: 100%;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);

  :deep(.own-segmented-control__option) {
    gap: 8px;
    padding: 6px 10px;
    white-space: nowrap;
  }
}

.hestia-admin-mode-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  max-width: 100%;
  padding: 6px 10px;
  border: 1px solid var(--divider);
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  color: var(--text-color-primary);
  background: var(--background-primary);
  font-size: 13px;
  font-weight: 600;
  line-height: 1;

  &:hover {
    background: var(--background-secondary);
  }
}

.hestia-admin-mode-trigger-chevron {
  width: 8px;
  height: 8px;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(225deg);
  transition: transform 0.12s ease;
}

.hestia-admin-mode-trigger-chevron--expanded {
  transform: rotate(45deg);
}

.hestia-iframe-error {
  padding: 12px 16px;
  font-size: 14px;
  color: var(--status-danger);
  background-color: var(--background-danger);
}

.hestia-iframe-loading {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
}

.hestia-iframe {
  width: 100%;
  flex: 1;
  min-height: 0;
  border: none;
  border-radius: var(--radius-regular);

  &--hidden {
    visibility: hidden;
    position: absolute;
  }
}
</style>
